import type { SiteData } from '../shared/types/site.js';

export type Lang = 'en' | 'sq';

const STORAGE_KEY = 'arkron-lang';

const translations: Record<Lang, Record<string, string>> = {
  en: {
    'nav.home': 'Home',
    'nav.services': 'Services',
    'nav.technologies': 'Technologies',
    'nav.career': 'Career',
    'nav.about': 'About us',
    'nav.contact': 'Contact Us',
    'lang.english': 'English',
    'lang.shqip': 'Shqip',
    'home.services': 'Services',
    'home.industries.cta': 'All industries',
    'home.industries.aria': 'Our focused industries',
    'home.company.label': 'Our company',
    'home.company.cta': 'Read More',
    'home.company.image.alt': 'Our workspace',
    'home.contact.cta': 'Contact Us',
    'home.contact.prefix': "Let's",
    'home.contact.image.alt': 'Collaborate',
    'services.hero.label': 'Services',
    'services.hero.title': 'Digital services',
    'services.hero.intro':
      'Design, build, and scale with clear delivery and strong ownership.',
    'services.card.cta': 'Explore service',
    'services.cta.title': 'Ready to launch or scale your product?',
    'services.cta.intro':
      "If you're weighing outsourcing or a dedicated team, we'll help you choose the right path.",
    'services.cta.button': 'Book a consultation',
    'service.detail.label': 'Service',
    'service.detail.notfound.title': 'Service not found',
    'service.detail.notfound.body':
      'The service you are looking for is not listed. Explore our services to find the right fit.',
    'service.detail.notfound.cta': 'Back to services',
    'service.detail.start': 'Start a project',
    'service.detail.how': 'How we work',
    'service.detail.lifecycle': 'Product lifecycle',
    'service.detail.ready': 'Ready to build with {service}?',
    'service.detail.ready.body':
      "Tell us about your idea and we'll map the best path forward.",
    'service.detail.ready.cta': 'Book a consultation',
    'service.detail.other.title': 'Explore other services',
    'service.detail.other.body':
      'Explore additional capabilities we offer to support your roadmap.',
    'service.detail.other.cta': 'Explore service',
    'ourwork.label': 'Our Work',
    'ourwork.title': 'Built with care, made to last.',
    'ourwork.card.cta': 'See project',
    'ourwork.cta.title': 'A partner team, not a vendor',
    'ourwork.cta.body':
      'Great products come from alignment, not handoffs. We work as part of your team with shared ownership.',
    'ourwork.cta.button': 'About us',
    'technologies.label': 'Technologies',
    'technologies.title': 'Tools chosen with intent',
    'technologies.intro':
      'We select the right technologies for the challenge, then execute with speed, quality, and care.',
    'technologies.cta.title': 'Open to meaningful collaborations',
    'technologies.cta.body':
      "If you're working on a mission-driven initiative, we'd love to explore how we can help.",
    'technologies.cta.button': 'Start a conversation',
    'career.label': 'Career',
    'career.title': 'Join a team that builds serious, powerful software',
    'career.intro':
      "We work with focus, respect, and high standards. If you want meaningful work and real growth, you'll feel at home.",
    'career.perks.title': 'Benefits that respect your time',
    'career.perks.intro': 'Designed for deep work, balance, and long-term sustainability.',
    'career.benefit.annual.title': 'Paid Annual Leave',
    'career.benefit.annual.body':
      '20 days off plus two extra days for each year at Arkron, up to 30 total.',
    'career.benefit.remote.title': '36-Hour Hybrid Week',
    'career.benefit.remote.body': 'A lighter hybrid work rhythm designed for focus, flexibility, and sustainable output across the week.',
    'career.benefit.family.title': 'Family First',
    'career.benefit.family.body': 'Married couples get one extra remote day per child each week, up to 4 remote days in total.',
    'career.benefit.parental.title': 'Paid Parental Leave',
    'career.benefit.parental.body': 'Paid leave for new parents so you can focus on what matters.',
    'career.benefit.sabbatical.title': 'Paid Sabbatical',
    'career.benefit.sabbatical.body': 'Extended paid time off after years of service to recharge or learn.',
    'career.benefit.women.title': 'Women in IT',
    'career.benefit.women.body': '4-hour workdays, with an option to extend up to 6 hours by request.',
    'career.benefit.pension.title': 'Competitive Pension',
    'career.benefit.pension.body': 'We invest in your long-term stability with pension contributions.',
    'career.benefit.health.title': 'Private Health Care',
    'career.benefit.health.body': 'Comprehensive coverage so you can focus on your work.',
    'career.benefit.wellbeing.title': 'Wellbeing and Development',
    'career.benefit.wellbeing.body':
      'Annual budget for local events, library membership, and culture.',
    'career.benefit.reviews.title': 'Pay Reviews',
    'career.benefit.reviews.body': 'Transparent, regular reviews to keep compensation fair.',
    'career.benefit.projects.title': 'Challenging Projects',
    'career.benefit.projects.body': 'Complex problems, modern stacks, and room to build with intent.',
    'career.benefit.stack.title': 'Latest Tech Stack',
    'career.benefit.stack.body': 'We keep tools current so you ship clean, maintainable software.',
    'career.open.title': 'Open positions',
    'career.open.body':
      "We don't have open roles today, but we're always interested in exceptional talent. If you think you're a fit, reach out.",
    'career.open.cta': 'Send an open application',
    'career.blogs.title': 'Read our insights and product notes',
    'career.blogs.cta': 'Read the articles',
    'about.label': 'About us',
    'about.title': 'We build technology that feels effortless',
    'about.intro':
      'We design and deliver software for teams that care about quality, clarity, and long-term impact.',
    'about.why': 'Why?',
    'about.body.p1':
      'We focus on web, software, and mobile as the core pillars of digital growth - turning ideas into products people love.',
    'about.body.p2':
      'ARKRON blends strategy, design, and engineering to deliver quality that lasts and experiences that feel well made.',
    'about.body.p3':
      'Grounded in disciplined execution, we help teams modernize, scale, and launch with confidence.',
    'about.cta.title': "Let's build something remarkable",
    'about.cta.body':
      "If you're planning a new product or a major upgrade, we're ready to help.",
    'about.cta.button': 'Get in touch',
    'contact.label': 'Contact',
    'contact.title': "Let's build together",
    'contact.intro':
      "Tell us about your project, timeline, and goals. We'll get back to you quickly.",
    'contact.form.title': "We'd love to hear from you",
    'contact.form.topic': 'Topics to talk (optional)',
    'contact.form.general': 'General',
    'contact.form.job': 'Job Application',
    'contact.form.other': 'Other',
    'contact.form.email': 'Email',
    'contact.form.phone': 'Phone number',
    'contact.form.message': 'Message',
    'contact.form.message.placeholder': 'Your message...',
    'contact.form.email.placeholder': 'you@email.com',
    'contact.form.phone.placeholder': '+383 44 123 456',
    'contact.form.send': 'Send',
    'contact.address': 'Address',
    'contact.viewmaps': 'View in Google Maps',
    'contact.email': 'Email',
    'contact.map.title': 'ARKRON location',
    'contact.success': "Thank you! We'll get back to you shortly.",
  },
  sq: {
    'nav.home': 'Kryefaqe',
    'nav.services': 'Shërbime',
    'nav.technologies': 'Teknologji',
    'nav.career': 'Karrierë',
    'nav.about': 'Rreth nesh',
    'nav.contact': 'Na Kontaktoni',
    'lang.english': 'English',
    'lang.shqip': 'Shqip',
    'home.services': 'Shërbime',
    'home.industries.cta': 'Të gjitha industritë',
    'home.industries.aria': 'Industritë tona të fokusuara',
    'home.company.label': 'Kompania jonë',
    'home.company.cta': 'Lexo më shumë',
    'home.company.image.alt': 'Hapësira jonë e punës',
    'home.contact.cta': 'Na Kontaktoni',
    'home.contact.prefix': 'Le të',
    'home.contact.image.alt': 'Bashkëpunim',
    'services.hero.label': 'Shërbime',
    'services.hero.title': 'Shërbime digjitale',
    'services.hero.intro':
      'Dizajnim, ndërtim dhe shkallëzim me dorëzim të qartë dhe pronësi të fortë.',
    'services.card.cta': 'Shiko shërbimin',
    'services.cta.title': 'Gati të lançoni ose të shkallëzoni produktin?',
    'services.cta.intro':
      'Nëse po mendoni për outsourcing ose ekip të dedikuar, ju ndihmojmë të zgjidhni rrugën e duhur.',
    'services.cta.button': 'Rezervo konsultim',
    'service.detail.label': 'Shërbim',
    'service.detail.notfound.title': 'Shërbimi nuk u gjet',
    'service.detail.notfound.body':
      'Shërbimi që po kërkoni nuk është i listuar. Eksploroni shërbimet tona për të gjetur përshtatjen e duhur.',
    'service.detail.notfound.cta': 'Kthehu te shërbimet',
    'service.detail.start': 'Nis një projekt',
    'service.detail.how': 'Si punojmë',
    'service.detail.lifecycle': 'Cikli i dorëzimit',
    'service.detail.ready': 'Gati për të ndërtuar me {service}?',
    'service.detail.ready.body':
      'Na tregoni për idenë tuaj dhe do të hartojmë rrugën më të mirë përpara.',
    'service.detail.ready.cta': 'Rezervo konsultim',
    'service.detail.other.title': 'Shërbime të tjera',
    'service.detail.other.body':
      'Zbuloni kapacitete të tjera që mbështesin roadmap-in tuaj.',
    'service.detail.other.cta': 'Shiko shërbimin',
    'ourwork.label': 'Puna jonë',
    'ourwork.title': 'E punuar me kujdes, e ndërtuar për qëndrueshmëri.',
    'ourwork.card.cta': 'Shiko projektin',
    'ourwork.cta.title': 'Partnerë të vërtetë, jo vetëm ofrues',
    'ourwork.cta.body':
      'Produktet e mira vijnë nga bashkëpunimi i ngushtë. Ne punojmë si pjesë e ekipit tuaj.',
    'ourwork.cta.button': 'Rreth nesh',
    'technologies.label': 'Teknologji',
    'technologies.title': 'Teknologji të zgjedhura me kujdes',
    'technologies.intro':
      'Zgjedhim mjetet që i përshtaten problemit dhe i zbatojmë me shpejtësi, cilësi dhe kujdes.',
    'technologies.cta.title': 'Të hapur për bashkëpunime domethënëse',
    'technologies.cta.body':
      'Nëse po punoni në një iniciativë me mision, do të donim të flisnim.',
    'technologies.cta.button': 'Nis bisedën',
    'career.label': 'Karrierë',
    'career.title': 'Bëhu pjesë e një ekipi që ndërton softuer të qetë e të fuqishëm',
    'career.intro':
      'Punojmë me fokus, respekt dhe standarde të larta. Nëse kërkon punë me kuptim dhe rritje reale, je në vendin e duhur.',
    'career.perks.title': 'Përfitime që respektojnë kohën tënde',
    'career.perks.intro': 'Të dizajnuara për punë të thellë, balancë dhe qëndrueshmëri.',
    'career.benefit.annual.title': 'Pushimi Vjetor',
    'career.benefit.annual.body':
      '20 ditë pushimi + dy ditë shtesë për çdo vit qëndrimi në Arkron, deri në 30 gjithsej.',
    'career.benefit.remote.title': 'Java Hibride 36-Orëshe',
    'career.benefit.remote.body': 'Një ritëm më i lehtë hibrid pune, i ndërtuar për fokus, fleksibilitet dhe qëndrueshmëri gjatë javës.',
    'career.benefit.family.title': 'Familja e Para',
    'career.benefit.family.body': 'Çiftet e martuara marrin nga 1 ditë pune prej shtëpisë shtesë për çdo fëmijë, deri në 4 ditë pune prej shtëpisë gjithsej.',
    'career.benefit.parental.title': 'Leje e Paguar Prindërore',
    'career.benefit.parental.body': 'Leje e paguar për prindërit e rinj që të fokusohen te më e rëndësishmja.',
    'career.benefit.sabbatical.title': 'Sabbatical i Paguar',
    'career.benefit.sabbatical.body': "Leje e zgjatur pas viteve të shërbimit për t'u rikarikuar ose mësuar.",
    'career.benefit.women.title': 'Gra në IT',
    'career.benefit.women.body': 'Orar 4-orësh, me mundësi zgjerimi deri në 6 orë me kërkesë.',
    'career.benefit.pension.title': 'Pension Konkurrues',
    'career.benefit.pension.body': 'Investojmë në stabilitetin afatgjatë me kontribute pensionale.',
    'career.benefit.health.title': 'Kujdes Shëndetësor Privat',
    'career.benefit.health.body': 'Mbulim i plotë që të fokusoheni në zanatin tuaj.',
    'career.benefit.wellbeing.title': 'Mirëqenie dhe Zhvillim',
    'career.benefit.wellbeing.body':
      'Buxhet vjetor për evente lokale, bibliotekë dhe kulturë.',
    'career.benefit.reviews.title': 'Rishikime të Pagës',
    'career.benefit.reviews.body': 'Rishikime të rregullta dhe transparente për pagesë të drejtë.',
    'career.benefit.projects.title': 'Projekte Sfiduese',
    'career.benefit.projects.body': 'Probleme komplekse, stack modern dhe hapësirë për të ndërtuar me qëllim.',
    'career.benefit.stack.title': 'Teknologji të Fundit',
    'career.benefit.stack.body': 'Mbajmë mjetet moderne për të dorëzuar softuer të mirëmbajtshëm.',
    'career.open.title': 'Pozita të hapura',
    'career.open.body':
      'Nuk kemi pozita të hapura për momentin, por gjithmonë kërkojmë talente të jashtëzakonshme. Na shkruani.',
    'career.open.cta': 'Dërgo aplikim të hapur',
    'career.blogs.title': 'Lexoni insight-et dhe shënimet tona',
    'career.blogs.cta': 'Lexo artikujt',
    'about.label': 'Rreth nesh',
    'about.title': 'Ne ndërtojmë teknologji që ndihet e lehtë',
    'about.intro':
      'Dizajnojmë dhe dorëzojmë softuer për ekipe që vlerësojnë cilësinë, qartësinë dhe ndikimin afatgjatë.',
    'about.why': 'Pse?',
    'about.body.p1':
      'Fokusohemi në web, softuer dhe mobile si shtylla të rritjes digjitale - ide të kthyer në produkte që njerëzit i duan.',
    'about.body.p2':
      'ARKRON bashkon strategjinë, dizajnin dhe inxhinierinë për cilësi që zgjat dhe përvoja të punuara mirë.',
    'about.body.p3':
      'Me standarde të larta, i ndihmojmë ekipet të modernizojnë, të shkallëzojnë dhe të dorëzojnë me besim.',
    'about.cta.title': 'Le të ndërtojmë diçka të veçantë',
    'about.cta.body':
      'Nëse po planifikoni një produkt të ri ose një përmirësim të madh, jemi gati të ndihmojmë.',
    'about.cta.button': 'Na kontaktoni',
    'contact.label': 'Kontakt',
    'contact.title': 'Le të ndërtojmë së bashku',
    'contact.intro':
      "Na tregoni për projektin, afatin dhe qëllimet. Do t'ju përgjigjemi shpejt.",
    'contact.form.title': 'Do të donim të dëgjonim nga ju',
    'contact.form.topic': 'Tema për të diskutuar (opsionale)',
    'contact.form.general': 'Të përgjithshme',
    'contact.form.job': 'Aplikim Pune',
    'contact.form.other': 'Tjetër',
    'contact.form.email': 'Email',
    'contact.form.phone': 'Numër telefoni',
    'contact.form.message': 'Mesazh',
    'contact.form.message.placeholder': 'Mesazhi juaj...',
    'contact.form.email.placeholder': 'ju@email.com',
    'contact.form.phone.placeholder': '+383 44 123 456',
    'contact.form.send': 'Dërgo',
    'contact.address': 'Adresa',
    'contact.viewmaps': 'Shiko në Google Maps',
    'contact.email': 'Email',
    'contact.map.title': 'Lokacioni i ARKRON',
    'contact.success': "Faleminderit! Do t'ju kontaktojmë së shpejti.",
  },
};

let currentLang: Lang = 'en';
const listeners: Array<(lang: Lang) => void> = [];

function loadLanguage(): Lang {
  const saved = localStorage.getItem(STORAGE_KEY);
  return saved === 'sq' ? 'sq' : 'en';
}

export function getLanguage(): Lang {
  return currentLang;
}

export function setLanguage(lang: Lang): void {
  if (lang === currentLang) return;
  currentLang = lang;
  localStorage.setItem(STORAGE_KEY, lang);
  listeners.forEach((fn) => fn(lang));
}

export function onLanguageChange(fn: (lang: Lang) => void): () => void {
  listeners.push(fn);
  return () => {
    const idx = listeners.indexOf(fn);
    if (idx >= 0) listeners.splice(idx, 1);
  };
}

export function initLanguage(): Lang {
  currentLang = loadLanguage();
  return currentLang;
}

export function t(key: string, vars?: Record<string, string>): string {
  const dict = translations[currentLang] ?? translations.en;
  let text = dict[key] ?? translations.en[key] ?? key;
  if (vars) {
    Object.entries(vars).forEach(([k, v]) => {
      text = text.replaceAll(`{${k}}`, v);
    });
  }
  return text;
}

export function bindLanguageToggle(): void {
  const buttons = Array.from(document.querySelectorAll<HTMLButtonElement>('[data-lang-btn]'));
  if (buttons.length === 0) return;
  const sync = (lang: Lang) => {
    buttons.forEach((btn) => {
      const isActive = btn.dataset.lang === lang;
      btn.classList.toggle('active', isActive);
      btn.setAttribute('aria-pressed', String(isActive));
    });
  };
  buttons.forEach((btn) => {
    btn.addEventListener('click', () => {
      const lang = (btn.dataset.lang as Lang) || 'en';
      setLanguage(lang);
    });
  });
  sync(currentLang);
  onLanguageChange(sync);
}

export function applyStaticTranslations(): void {
  document.querySelectorAll<HTMLElement>('[data-i18n]').forEach((el) => {
    const key = el.dataset.i18n;
    if (key) el.textContent = t(key);
  });
}

export function getCareerBenefits(): Array<{ title: string; description: string }> {
  return [
    { title: t('career.benefit.annual.title'), description: t('career.benefit.annual.body') },
    { title: t('career.benefit.remote.title'), description: t('career.benefit.remote.body') },
    { title: t('career.benefit.family.title'), description: t('career.benefit.family.body') },
    { title: t('career.benefit.parental.title'), description: t('career.benefit.parental.body') },
    { title: t('career.benefit.sabbatical.title'), description: t('career.benefit.sabbatical.body') },
    { title: t('career.benefit.women.title'), description: t('career.benefit.women.body') },
    { title: t('career.benefit.pension.title'), description: t('career.benefit.pension.body') },
    { title: t('career.benefit.health.title'), description: t('career.benefit.health.body') },
    { title: t('career.benefit.wellbeing.title'), description: t('career.benefit.wellbeing.body') },
    { title: t('career.benefit.reviews.title'), description: t('career.benefit.reviews.body') },
    { title: t('career.benefit.projects.title'), description: t('career.benefit.projects.body') },
    { title: t('career.benefit.stack.title'), description: t('career.benefit.stack.body') },
  ];
}

type TranslationMap = Record<string, { title?: string; description?: string }>;

const serviceSq: TranslationMap = {
  'web-development': {
    title: 'Zhvillim Uebi',
    description:
      'Zgjidhje të personalizuara uebi që rrisin efikasitetin dhe shfaqin vlerën unike të markës suaj.',
  },
  'mobile-app': {
    title: 'Aplikacione Mobile',
    description:
      'Aplikacione mobile të punuara me kujdes që ofrojnë përvojë të pandërprerë dhe fuqizojnë angazhimin.',
  },
  'creative-design-uiux': {
    title: 'Dizajn Kreativ (UI/UX)',
    description:
      'Dizajne tërheqëse dhe të fokusuar te përdoruesi që kombinojnë thjeshtësi dhe bukuri.',
  },
  consulting: {
    title: 'Konsulencë',
    description:
      'Këshillim strategjik për të përafruar outsourcing-un e IT me qëllimet tuaja.',
  },
  'maintenance-and-support': {
    title: 'Mirëmbajtje dhe Mbështetje',
    description:
      "Mbështetje teknike e besueshme për t'i mbajtur sistemet të përditësuara, të sigurta dhe funksionale.",
  },
  'ai-futuristic': {
    title: 'AI Futuristike',
    description:
      'Zgjidhje inovative me AI për të automatizuar proceset dhe të ardhmen e biznesit tuaj.',
  },
};

const industrySq: TranslationMap = {
  'ai-futuristic-ml': {
    title: 'AI futuristike & ML',
    description: 'Zgjidhje AI dhe ML që automatizojnë dhe optimizojnë biznesin tuaj.',
  },
  agriculture: {
    title: 'Bujqësi',
    description: 'Mjete digjitale për bujqësi precize, zinxhir furnizimi dhe inovacion agri-tech.',
  },
  'real-estate': {
    title: 'Patundshmëri',
    description: 'Platforma për menaxhim pronash, lista, ture virtuale dhe rrjedha pune smart.',
  },
  'social-networking': {
    title: 'Rrjete sociale',
    description: 'Platforma komunitare dhe aplikacione sociale të ndërtuara për shkallë.',
  },
  'retail-ecommerce': {
    title: 'Retail & E-commerce',
    description: 'Dyqane online dhe teknologji retail që rrisin shitjet dhe përvojën.',
  },
  gaming: {
    title: 'Gaming',
    description: 'Produkte dhe sisteme lojërash të ndërtuara për angazhim dhe shkallëzim.',
  },
};

const projectSq: TranslationMap = {
  'arkron-platform': {
    title: 'Platforma ARKRON',
    description:
      'Sisteme të strukturuara dhe të mirëmbajtshme për rritje afatgjatë. Ndërfaqe të qeta dhe inxhinieri e qëndrueshme.',
  },
  'native-experience': {
    title: 'Përvojë Native',
    description:
      'Aplikacione native dhe cross-platform me performancë të lartë dhe qasje të kujdesshme.',
  },
  'smart-systems': {
    title: 'Sisteme Inteligjente',
    description:
      'Inteligjencë e aplikuar aty ku ka rëndësi. Zgjidhje smart për të ardhmen e biznesit tuaj.',
  },
};

const techCategorySq: Record<string, string> = {
  Mobile: 'Mobile',
  Frontend: 'Frontend',
  Backend: 'Backend',
  'UI/UX': 'UI/UX',
  Cloud: 'Cloud',
  Databases: 'Baza të të dhënave',
  Product: 'Produkt',
  'Game Development': 'Zhvillim lojërash',
};

export function localizeSiteData(siteData: SiteData, lang: Lang): SiteData {
  if (lang === 'en') return siteData;

  const hero = {
    line1: 'Ne dizajnojmë',
    line2: 'dhe ndërtojmë',
    line3: 'avantazh digjital.',
    tagline:
      'Nga strategjia në lançim, krijojmë produkte digjitale që janë të qarta, të shpejta dhe të besueshme.',
  };

  const services = (siteData.services ?? []).map((s) => ({
    ...s,
    title: serviceSq[s.slug]?.title ?? s.title,
    description: serviceSq[s.slug]?.description ?? s.description,
  }));

  const industries = siteData.industries
    ? {
        ...siteData.industries,
        title: 'Industritë që njohim mirë',
        intro:
          'Bashkëpunojmë me ekipe në sektorë të ndryshëm për transformim digjital të përshtatur.',
        items: (siteData.industries.items ?? []).map((i) => ({
          ...i,
          title: industrySq[i.slug]?.title ?? i.title,
          description: industrySq[i.slug]?.description ?? i.description,
        })),
      }
    : undefined;

  const ourWork = siteData.ourWork
    ? {
        ...siteData.ourWork,
        title: 'Puna jonë kryesore',
        intro:
          'Një përzgjedhje projektesh që bashkojnë strategjinë, mjeshtërinë dhe ndikimin.',
        projects: (siteData.ourWork.projects ?? []).map((p) => ({
          ...p,
          category: p.category,
          name: projectSq[p.slug]?.title ?? p.name,
          description: projectSq[p.slug]?.description ?? p.description,
        })),
      }
    : undefined;

  const company = siteData.company
    ? {
        ...siteData.company,
        title: 'Kush jemi',
        body:
          'ARKRON është studio produkti dhe inxhinierie që ndihmon ekipet ambicioze të dërgojnë softuer të guximshëm.',
      }
    : undefined;

  const contact = siteData.contact
    ? {
        ...siteData.contact,
        titleLine2: 'ndërtojmë',
        subtitle: 'Jemi gati të dëgjojmë idetë tuaja',
        cta: 'Na Kontaktoni',
      }
    : undefined;

  const technologies = siteData.technologies
    ? {
        ...siteData.technologies,
        title: 'Teknologji të zgjedhura me kujdes',
        intro:
          'Zgjedhim mjetet që i përshtaten problemit dhe i zbatojmë me shpejtësi, cilësi dhe kujdes.',
        categories: (siteData.technologies.categories ?? []).map((cat) => ({
          ...cat,
          name: techCategorySq[cat.name] ?? cat.name,
        })),
      }
    : undefined;

  const footer = siteData.footer
    ? { ...siteData.footer, text: '© 2026 ARKRON - Mbi qiell, nën tokë' }
    : undefined;

  return {
    ...siteData,
    hero,
    services,
    industries,
    ourWork,
    company,
    contact,
    technologies,
    footer,
  };
}
